// One response in full, opened from its row, in the vouchers drawer shape:
// a bordered header (overline + close, title, chips), then Eyebrow-headed
// body sections of facts, the ids under a closed "Technical details".

import { Badge, Button, CopyButton, Eyebrow, FactList, Sheet } from "@pej/kore/components";
import { ChevronRight, X } from "lucide-react";
import { type ReactNode, useState } from "react";
import { useResponse } from "@/api/responses";
import type { ResponseRow, Survey } from "@/api/types";
import { metricStep } from "@/features/dashboard/stats-mapping";
import { answerFormatter, formatSubmittedAt, ScoreBadge } from "./response-format";
import { optionByScore, sortedSteps, stepLabel } from "./responses-filters";

function Section({ title, children }: { title: string; children: ReactNode }) {
    return (
        <section className="space-y-3">
            <Eyebrow as="h3" size="lg">
                {title}
            </Eyebrow>
            <div className="space-y-3">{children}</div>
        </section>
    );
}

function MonoId({ value, label }: { value: string; label: string }) {
    return (
        <span className="inline-flex max-w-full items-center gap-1">
            <span className="truncate font-mono text-xs">{value}</span>
            <span className="shrink-0">
                <CopyButton value={value} label={`Copy ${label}`} />
            </span>
        </span>
    );
}

export function ResponseSheet({
    survey,
    row,
    canVerify,
    onClose,
    onDelete,
}: {
    survey: Survey;
    /** The table's row, shown at once; null closes the panel. */
    row: ResponseRow | null;
    canVerify: boolean;
    onClose: () => void;
    onDelete: (row: ResponseRow) => void;
}) {
    const detail = useResponse(row?.id ?? null);
    const [showTech, setShowTech] = useState(false);
    const scored = metricStep(survey);
    const attributes = Object.entries(detail.data?.attributes ?? {});

    return (
        <Sheet open={row !== null} onOpenChange={(open) => !open && onClose()}>
            <Sheet.Content side="right" className="flex flex-col gap-0 overflow-hidden sm:max-w-lg">
                {row && (
                    <>
                        <Sheet.Title className="sr-only">Response</Sheet.Title>
                        <Sheet.Description className="sr-only">
                            Submitted {formatSubmittedAt(row.submittedAt)} through {row.placementName}
                        </Sheet.Description>
                        <div className="border-b px-6 pt-6 pb-4">
                            <div className="mb-1.5 flex min-h-7 items-center justify-between gap-3">
                                <Eyebrow as="p">Response</Eyebrow>
                                <Sheet.Close
                                    render={
                                        <Button variant="ghost" size="icon" aria-label="Close" className="-mr-2">
                                            <X className="size-4" />
                                        </Button>
                                    }
                                />
                            </div>
                            <p className="text-lg font-semibold tracking-tight">{formatSubmittedAt(row.submittedAt)}</p>
                            <div className="mt-2 flex flex-wrap gap-2">
                                {scored && <ScoreBadge score={row.score} meta={optionByScore(scored)} />}
                                <Badge tone={row.completed ? "success" : "neutral"}>
                                    {row.completed ? "Completed" : "Partial"}
                                </Badge>
                                {canVerify && row.contextTrusted && <Badge tone="accent">Verified</Badge>}
                            </div>
                        </div>

                        <Sheet.Body className="space-y-5">
                            <Section title="Details">
                                <FactList>
                                    <FactList.Fact label="Placement">{row.placementName}</FactList.Fact>
                                    <FactList.Fact label="Location">{row.locationRef ?? "—"}</FactList.Fact>
                                    <FactList.Fact label="Respondent">
                                        {row.respondentRef ? (
                                            <MonoId value={row.respondentRef} label="respondent" />
                                        ) : (
                                            "Anonymous"
                                        )}
                                    </FactList.Fact>
                                    {row.reference !== null && (
                                        <FactList.Fact label="Reference">
                                            <MonoId value={row.reference} label="reference" />
                                        </FactList.Fact>
                                    )}
                                </FactList>
                            </Section>

                            <Section title="Answers">
                                {sortedSteps(survey).map((step, index) => {
                                    const text = answerFormatter(step)(
                                        row.answers.find((answer) => answer.stepId === step.id),
                                    );
                                    return (
                                        <div key={step.id} className="space-y-0.5">
                                            <p className="text-xs text-kore-text-muted">{stepLabel(index, step)}</p>
                                            <p
                                                className={`whitespace-pre-wrap text-sm ${text === null ? "text-kore-text-muted" : ""}`}
                                            >
                                                {text ??
                                                    (step.position > row.furthestPosition ? "Not reached" : "Skipped")}
                                            </p>
                                        </div>
                                    );
                                })}
                            </Section>

                            <Section title="Attributes">
                                {detail.isPending ? (
                                    <p className="text-sm text-kore-text-muted">Loading…</p>
                                ) : detail.isError ? (
                                    <p className="text-sm text-kore-text-muted">Couldn't load the attributes.</p>
                                ) : attributes.length === 0 ? (
                                    <p className="text-sm text-kore-text-muted">The page sent none.</p>
                                ) : (
                                    <FactList>
                                        {attributes.map(([key, values]) => (
                                            <FactList.Fact key={key} label={key}>
                                                {values.join(", ")}
                                            </FactList.Fact>
                                        ))}
                                    </FactList>
                                )}
                            </Section>

                            <div>
                                <button
                                    type="button"
                                    onClick={() => setShowTech((open) => !open)}
                                    aria-expanded={showTech}
                                    aria-controls="response-tech"
                                    className="inline-flex items-center gap-1.5 text-sm font-medium text-kore-text-muted hover:text-kore-text"
                                >
                                    <ChevronRight
                                        className={`size-3.5 transition-transform ${showTech ? "rotate-90" : ""}`}
                                    />
                                    Technical details
                                </button>
                                {showTech && (
                                    <div id="response-tech" className="pt-3">
                                        <FactList>
                                            <FactList.Fact label="Response ID">
                                                <MonoId value={row.id} label="response id" />
                                            </FactList.Fact>
                                        </FactList>
                                    </div>
                                )}
                            </div>
                        </Sheet.Body>

                        <Sheet.Footer className="border-t">
                            <Button type="button" variant="destructive" onClick={() => onDelete(row)}>
                                Delete response
                            </Button>
                        </Sheet.Footer>
                    </>
                )}
            </Sheet.Content>
        </Sheet>
    );
}
